/**
 * AI profile store — mirrors Android's SharedPreferences "ai_profiles".
 * Profiles live in localStorage under the prefix shell.ai.
 *
 * Key pattern: shell.ai.<profileName>_key | _model | _provider
 */

const PREFIX = 'shell.ai.';

export interface AiProfile {
  key: string;
  model: string;
  provider: string;
}

/** Returns all profiles as a name → profile map. */
export function getAiProfiles(): Record<string, AiProfile> {
  const profiles: Record<string, AiProfile> = {};
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k?.startsWith(PREFIX)) continue;
    const rest = k.slice(PREFIX.length);
    const underscoreIdx = rest.lastIndexOf('_');
    if (underscoreIdx < 0) continue;
    const name = rest.slice(0, underscoreIdx);
    const field = rest.slice(underscoreIdx + 1);
    if (!['key', 'model', 'provider'].includes(field)) continue;
    if (!name) continue;
    if (!profiles[name]) profiles[name] = { key: '', model: 'gemini-2.0-flash', provider: 'gemini' };
    (profiles[name] as unknown as Record<string, string>)[field] = localStorage.getItem(k) ?? '';
  }
  return profiles;
}

/** Returns a single profile, or null if not found. */
export function getAiProfile(name: string): AiProfile | null {
  const key = localStorage.getItem(`${PREFIX}${name}_key`);
  if (key === null) return null;
  return {
    key,
    model: localStorage.getItem(`${PREFIX}${name}_model`) ?? 'gemini-2.0-flash',
    provider: localStorage.getItem(`${PREFIX}${name}_provider`) ?? 'gemini',
  };
}

/** Saves or updates a profile. */
export function setAiProfile(name: string, key: string, model: string, provider: string): void {
  localStorage.setItem(`${PREFIX}${name}_key`, key);
  localStorage.setItem(`${PREFIX}${name}_model`, model);
  localStorage.setItem(`${PREFIX}${name}_provider`, provider);
}

/** Deletes a profile. No-op if it doesn't exist. */
export function deleteAiProfile(name: string): void {
  localStorage.removeItem(`${PREFIX}${name}_key`);
  localStorage.removeItem(`${PREFIX}${name}_model`);
  localStorage.removeItem(`${PREFIX}${name}_provider`);
}

/** Default model IDs per provider — shown in the add-profile UI. */
export const DEFAULT_MODELS: Record<string, string> = {
  gemini:    'gemini-2.0-flash',
  anthropic: 'claude-3-5-haiku-20241022',
  openai:    'gpt-4o-mini',
};
